import { Divider, Flex, Form, Input, Spin, Typography } from "antd";

import { observer } from "mobx-react-lite";
import { useStores } from "stores";
import { CustomButton, DefaultInput } from "ui";
import { _greaterOrEqual, _isNotLongerThan, _isRequired, requestWithNotify } from "utils";

import { FORM_VALIDATION_DEBOUNCE } from "constants/form";
import { useNotificationContext } from "providers";
import { NOTIFICATION_TYPE_ENUM } from "interfaces";
import { useCallback, useState } from "react";
import { ACTION_TYPE_ENUM } from "./actions";

import { NOMINAL_ACCOUNT_REQUESTS_DELAY } from "constants/nominalAccount";

export interface IColumnActionModalProps {
  bidId: string;
  actionType: keyof typeof ACTION_TYPE_ENUM;
}

export const ActionModal = observer(({ bidId, actionType }: IColumnActionModalProps) => {
  const { actionModals, nominalAccounts, rubWithdrawalRequests } = useStores();
  const { openNotification } = useNotificationContext();
  const [loading, setLoading] = useState(false);

  const [form] = Form.useForm();

  const proofOfPayment = Form.useWatch("proofOfPayment", form);

  const hasCancelType = actionType === ACTION_TYPE_ENUM.CANCEL;
  const hasCompleteType = actionType === ACTION_TYPE_ENUM.PROCESSING;

  const completeBtnDisabled = hasCompleteType && proofOfPayment <= 0;

  const handleCloseModal = useCallback(() => {
    actionModals.closeAll();
  }, [actionModals]);

  const fetchData = useCallback(async () => {
    const actionRequest: Record<string, () => Promise<any>> = {
      PROCESSING: () => {
        const formData = form.getFieldsValue(["proofOfPayment"]);
        return nominalAccounts.fetchCompleteRubWithdrawal(bidId, formData);
      },
      CANCEL: () => nominalAccounts.fetchCancelRubWithdrawal(bidId),
    };

    try {
      setLoading(true);

      const res = await requestWithNotify({
        fetchFn: actionRequest[actionType],
        openNotification,
        successMsg: `Заявка на вывод ${hasCompleteType ? "исполнена" : "отменена"}`,
        errorMsg: "Ошибка запроса. Попробуйте позже",
      });

      if (res.result) {
        requestWithNotify({
          fetchFn: () => nominalAccounts.fetchAllBalances({ rubOperationsFetch: false }),
          openNotification,
          delay: NOMINAL_ACCOUNT_REQUESTS_DELAY,
          successMsg: "Балансы обновлены",
        });
      }
    } finally {
      setTimeout(() => {
        form.resetFields(["proofOfPayment"]);
        handleCloseModal();
        setLoading(false);
      }, NOMINAL_ACCOUNT_REQUESTS_DELAY);
    }
  }, [actionType, form, handleCloseModal, hasCompleteType, openNotification, bidId, nominalAccounts]);

  return loading ? (
    <Flex justify="center" align="center" style={{ height: "100px" }}>
      <Spin />
    </Flex>
  ) : (
    <Flex vertical gap="large">
      {hasCompleteType && <Divider className="margin-top-none margin-bottom-none" />}
      <Form form={form} onFinish={fetchData}>
        {hasCompleteType && (
          <Form.Item
            name="proofOfPayment"
            initialValue={""}
            className="full-width"
            validateDebounce={FORM_VALIDATION_DEBOUNCE}
            rules={[_isRequired("Поле обязательно для ввода"), _greaterOrEqual(0), _isNotLongerThan(30)]}
          >
            <DefaultInput placeholder="Номер платежного поручения" />
          </Form.Item>
        )}

        {hasCancelType && (
          <div className="margin-bottom-lg">
            <Typography.Text>Вы не сможете её восстановить и понадобится создать новую</Typography.Text>
          </div>
        )}

        <Form.Item>
          <Flex gap="large">
            <CustomButton fullWidth onClick={handleCloseModal} variant={"outlined"}>
              {!hasCancelType ? "Отмена" : "Не отменять"}
            </CustomButton>
            <CustomButton fullWidth htmlType="submit" danger={hasCancelType} disabled={completeBtnDisabled}>
              {!hasCancelType ? "Подтвердить" : "Отменить заявку"}
            </CustomButton>
          </Flex>
        </Form.Item>
      </Form>
    </Flex>
  );
});
